import { useTranslation } from "@redotech/redo-i18n";
import {
  isReturnsSettingsTab,
  RETURNS_SETTINGS_TAB_QUERY_PARAM,
  ReturnsSettingsTab,
} from "@redotech/redo-settings-common/flat-team-settings/returns/returns-settings-tab";
import type { RedoHorizontalTab } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import { PageMainContentRefContext } from "@redotech/redo-web/page";
import { useCallback, useContext, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export const DEFAULT_RETURNS_SETTINGS_TAB = ReturnsSettingsTab.COVERAGE;

export function useReturnsSettingsTabs() {
  const { t } = useTranslation();
  return useMemo<RedoHorizontalTab<ReturnsSettingsTab>[]>(
    () => [
      { key: ReturnsSettingsTab.COVERAGE, label: t("Coverage") },
      { key: ReturnsSettingsTab.POLICIES, label: t("Policies & workflows") },
      { key: ReturnsSettingsTab.PORTAL, label: t("Portal") },
      { key: ReturnsSettingsTab.OPERATIONS, label: t("Operations") },
      { key: ReturnsSettingsTab.LABELS, label: t("Labels") },
      { key: ReturnsSettingsTab.LOCATIONS, label: t("Locations") },
      {
        key: ReturnsSettingsTab.SHOPPER_NOTIFICATIONS,
        label: t("Shopper notifications"),
      },
      { key: ReturnsSettingsTab.WAREHOUSE, label: t("Warehouse") },
    ],
    [t],
  );
}

/** The selected tab lives in the URL so links and reloads land on it. */
export function useSelectedReturnsSettingsTab(): [
  ReturnsSettingsTab,
  (tab: ReturnsSettingsTab) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollRef = useContext(PageMainContentRefContext);
  const tabParam = searchParams.get(RETURNS_SETTINGS_TAB_QUERY_PARAM);
  const selectedTab = isReturnsSettingsTab(tabParam)
    ? tabParam
    : DEFAULT_RETURNS_SETTINGS_TAB;
  const setSelectedTab = useCallback(
    (tab: ReturnsSettingsTab) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.set(RETURNS_SETTINGS_TAB_QUERY_PARAM, tab);
          return next;
        },
        { replace: true },
      );
      scrollRef?.current?.scrollTo({ top: 0 });
    },
    [setSearchParams, scrollRef],
  );
  return [selectedTab, setSelectedTab];
}
